import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { UserService, type UsersFilters } from '../api/user-service';
import type { PaginatedResult, PaginationDto } from '../types/common';
import type { AdminUserDoc } from '../types/user';

export const userQueryKeys = {
  all: ['users'] as const,
  list: (filters: UsersFilters = {}, pagination: PaginationDto = {}) =>
    ['users', 'list', filters, pagination] as const,
  byId: (id: string | null) => ['users', id] as const,
};

export function useUsers(
  filters?: UsersFilters,
  pagination?: PaginationDto,
): UseQueryResult<PaginatedResult<AdminUserDoc>> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: userQueryKeys.list(filters, pagination),
    queryFn: async () => {
      const result = await UserService.getUsers(filters, pagination);
      for (const user of result.items) {
        queryClient.setQueryData(userQueryKeys.byId(user._id), user);
      }
      return result;
    },
  });
}

export function useUser(id: string | null): UseQueryResult<AdminUserDoc> {
  return useQuery({
    queryKey: userQueryKeys.byId(id),
    queryFn: () => UserService.getUser(id as string),
    enabled: Boolean(id),
  });
}
